import { AgGridReactProps } from '@ag-grid-community/react';
import { Box, Grid } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { HandshakeIcon, Lock as LockIcon, Wallet as WalletIcon } from '@tassat/icon';
import { AttributeTag, ClosedSymbol } from '@tassat/symbols';
import React from 'react';
import { LinkProps, Link as RouterLink } from 'react-router-dom';

import { GridFirmData } from 'Containers/admin/pages/firms/firms';
import { HANDSHAKE_ICON_COLOR, LOCK_ICON_COLOR, NON_ALPHANUMERIC_REGEX } from 'Lib/constant';
import { ApplicationMuiTheme } from 'Lib/themes/muiTheme';

const Link = (RouterLink as unknown) as React.ComponentClass<LinkProps>;

interface IFirmListItemProps extends AgGridReactProps{
    data: GridFirmData;
    cellHeight: number;
}

const muiStyles = makeStyles((theme: ApplicationMuiTheme) => {
    const activeBorder = theme?.app?.clientTheme.colors.sidelist?.activeBorder ||
        theme?.app?.applicationColors.success ||
        '#A5D673';

    const disabledBorder = theme?.app?.clientTheme.colors.sidelist?.disabledBorder ||
        theme?.app?.applicationColors.failureLight ||
        '#FF9B6B';

    const closedBorder = theme?.app?.clientTheme.colors.sidelist?.closedBorder ||
        theme?.app?.applicationColors.fontLabelColor ||
        '#727272';

    return {
        root: {
            minHeight: '60px',
            maxHeight: '122px',
            height: (props: IFirmListItemProps) => props.cellHeight,
            color: 'inherit',
            textDecoration: 'inherit',
            borderLeft: (props: IFirmListItemProps) => `5px solid ${props.data.IsClosed ?
                closedBorder
                : (!props.data.IsLocked && !props.data.IsFrozen) ?
                    activeBorder
                    : disabledBorder}`,
            paddingLeft: '1rem',
            backgroundColor: (props: IFirmListItemProps) => ((props.data.IsLocked || props.data.IsFrozen) ?
                theme?.app?.applicationColors.tableRowWarningColor || 'rgba(245,130,74,0.16)'
                : theme?.app?.applicationColors.enumColors.white || '#fff'),
            cursor: 'pointer',
            '&:hover': {
                backgroundColor: theme?.app?.applicationColors.enumColors.offWhite ||
                    '#F3F3F3'
            }
        },
        name: {
            fontSize: '1.25rem',
            lineHeight: '1.5rem',
            paddingTop: '1rem',
            paddingBottom: '.25rem',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            overflow: 'hidden'
        },
        activity: {
            fontSize: '1.5rem',
            lineHeight: '2rem'
        },
        iconsContainer: { padding: '.75rem 0' },
        statusIcon: { marginRight: '.75rem' },
        lockIcon: {
            height: '22px',
            width: '22px'
        }
    };
});

export const FirmInfoCellRenderer: React.FC<IFirmListItemProps> = (props: IFirmListItemProps) => {
    const { data } = props;

    const {
        root,
        name,
        iconsContainer,
        statusIcon,
        lockIcon
    } = muiStyles(props);

    return (
        <Grid
            className={`${root} firm-info-cell`}
            component={Link}
            container
            direction='column'
            id={`${data.Name.replace(NON_ALPHANUMERIC_REGEX, '')}_firmInfoCell`}
            spacing={0}
            to={`/admin/firms/${data.FirmId}`}
            wrap='nowrap'
        >
            <Grid
                container
                item
            >
                <Box
                    className={name}
                    fontWeight='bold'
                    id={`${data.Name.replace(NON_ALPHANUMERIC_REGEX, '')}_firmInfoCell_firmName`}
                >
                    {data.Name}
                </Box>
            </Grid>
            <Grid
                alignItems='center'
                className={iconsContainer}
                container
                direction='row'
                item
                spacing={0}
            >
                {'HasApiConnections' in data && data.HasApiConnections ||
                    data.IsTokenAPIFirm ?
                        <AttributeTag
                            attribute='API'
                            className={statusIcon}
                            id={`${data.Name.replace(NON_ALPHANUMERIC_REGEX, '')}_firmInfoCell_firmApiStatus`}
                        />
                    : null}
                {data.IsLiquidityFacility &&
                    <AttributeTag
                        attribute='LF'
                        backgroundColor='#FDF9E2'
                        borderColor='#EDCB26'
                        className={statusIcon}
                        id={`${data.Name.replace(NON_ALPHANUMERIC_REGEX, '')}_firmInfoCell_firmLiquidityFacilityStatus`}
                    />}
                {data.IsCollateral && (
                    <HandshakeIcon
                        className={statusIcon}
                        iconColor={HANDSHAKE_ICON_COLOR}
                        id={`${data.Name.replace(NON_ALPHANUMERIC_REGEX, '')}_firmInfoCell_firmCollateralStatus`}
                    />
                )}
                {data.IsClosed &&
                    <ClosedSymbol
                        className={statusIcon}
                        id={`${data.Name.replace(NON_ALPHANUMERIC_REGEX, '')}_firmInfoCell_firmClosedStatus`}
                    />}
                {data.IsLocked &&
                    <LockIcon
                        className={`${statusIcon} ${lockIcon}`}
                        iconColor={LOCK_ICON_COLOR}
                        id={`${data.Name.replace(NON_ALPHANUMERIC_REGEX, '')}_firmInfoCell_firmLockedStatus`}
                    />}
                {data.IsFrozen &&
                    <WalletIcon
                        className={`${statusIcon} ${lockIcon}`}
                        iconColor={LOCK_ICON_COLOR}
                        id={`${data.Name.replace(NON_ALPHANUMERIC_REGEX, '')}_firmInfoCell_firmFrozenStatus`}
                    />}
            </Grid>
        </Grid>
    );
};

export default (FirmInfoCellRenderer);